/* eslint-disable react/forbid-prop-types,react/no-unstable-nested-components */
import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { BuiltInCustom } from 'relevant-shared/rtImporter/defaultGranularDims';
import { PREBID_CONFIGURATION_DIM } from 'relevant-shared/misc/sharedConstants';
import { isMongoIdLike } from 'relevant-shared/misc/details/diffFns';
import TextField from '../TextField';
import Form from '../../containers/Form';
import { PopupForm, wrapFnComponent, Scope, SimpleAlert } from '../Wrappers';
import TreeSelect from '../TreeSelect';
import Disabler from '../Disabler';
import SiteSelect from '../SiteSelect';
import {ActionButton} from '../ActionButton/ActionButton';

export const getBuiltIn = ({ name }) => !!_.find(BuiltInCustom, { hbDim: name });

/** name and value are normally set on the input-element, but as we'll use a TextField below without any input
 * we need to provide them in a <div> here instead so that form validation will work */
const TextFieldWrap = wrapFnComponent((props) => (
	<div
		ref={(elm) => {
			if (elm) {
				elm.value = props.value;
			}
		}}
		name={props.name}
	>
		<TextField
			{..._.omit(props, Form.metaFields)}
		/>
	</div>
));

const renderSelectBox = ({ model: { selected }, form }, { customStats, dim }) => {
	const APPROX_MAX_ELMS = 100;
	const byValStats = customStats[dim.name] || {};
	const total = _.sumBy(_.values(byValStats), 'pageViews');
	const preSelectMap = _.keyBy(dim.values);
	const opts = _.sortBy(_.uniq([...dim.values, ..._.keys(byValStats)]).map((name) => ({
		name,
		count: byValStats[name]?.pageViews || 0,
		isPreSelected: !!preSelectMap[name],
	})), (e) => -e.count).filter((e, idx) => idx < APPROX_MAX_ELMS || e.isPreSelected);
	const selectMap = _.keyBy(selected);
	const root = { name: 'All', opts };
	return (
		<TreeSelect
			rootNode={root}
			getChildren={(n) => n.opts}
			getLabel={(n) => n.name}
			getNodeId={(n) => (n === root ? 'all' : `_${n.name}`)}
			selectedObjects={opts.filter((o) => selectMap[o.name])}
			expandOnce={[root]}
			noSort
			onSelectionChange={(arr) => form.setVals({ selected: _.map(arr, 'name') })}
			render={(n) => (n === root ? root.name : (
				<>
					<span style={{ fontWeight: n.isPreSelected ? 'bold' : 'normal' }}>
						{n.name}&nbsp;
					</span>
					<span style={{ color: n.count ? 'green' : 'gray' }}>
						({((n.count / (total || 1)) * 100).toFixed(1)}%)
					</span>
				</>
			))}
		/>
	);
};

const renderPbConfigSelect = ({ model: { selected }, form }, { customStats, dim }) => {
	return (
		<Scope
			content={(scope, { missingIds }) => (
				<>
					{!!missingIds?.length && (
						<SimpleAlert
							severity="error"
							content={(
								<>
									There are currently <b>{missingIds.length}</b> value(s) for deleted
									prebid configurations. Press the button to remove these.
								</>
							)}
							action={(
								<ActionButton
									label="Remove values"
									variant="outlined"
									onClick={() => {
										form.setVals({ selected: _.difference(selected, missingIds) });
										scope.setState({ missingIds: null });
									}}
								/>
							)}
						/>
					)}
					<SiteSelect
						expanded
						type="programmatic"
						isPbConfigs
						selected={selected}
						onChange={(arr) => form.setVals({ selected: _.uniq([...arr, ...missingIds]) })}
						onLoaded={({ byId }) => scope.setState({ missingIds: selected.filter((s) => !byId[s]) })}
					/>
				</>
			)}
		/>
	);
};

export const requireWhenEnabled = (dim) => ({
	satisfy: {
		valid: (v) => !_.isEmpty(v) || !dim.enabled,
		message: 'Required when enabled',
		checkOnEmpty: true,
	},
});

const getRenderFn = (dim) => {
	if (dim.name === PREBID_CONFIGURATION_DIM) {
		return renderPbConfigSelect;
	}
	return renderSelectBox;
};

export function ValueSelector(props) {
	const { dim, field } = props;
	const form = Form.fromField(field);
	const isBuiltIn = !!getBuiltIn(dim);
	return (
		<TextFieldWrap
			label="Values to synchronize"
			style={{ minWidth: 200 }}
			{...field('values').validation(requireWhenEnabled(dim))}
			fullWidth
			inputLabelProps={{ shrink: true, style: { background: 'white' } }}
			outlinedInputProps={{
				inputComponent: forwardRef((__, ref) => (
					<div ref={ref}>
						<PopupForm
							linkTitle="Selected values"
							title={`Selected values ${isBuiltIn ? '(read-only)' : ''}`}
							okDisabled={isBuiltIn}
							nonSelectedString="(none)"
							size="md"
							linkStyle={{ margin: 18, color: 'darkblue' }}
							selected={dim.values}
							getModel={() => ({ selected: _.cloneDeep(dim.values) })}
							getSingleTitle={isMongoIdLike(dim.values[0]) ? undefined : (val) => val}
							content={(p) => (
								<Disabler disabled={isBuiltIn}>
									{getRenderFn(dim)(p, props)}
								</Disabler>
							)}
							onApplyChanges={({ model: { selected } }) => form.update(() => {
								dim.values = selected;
							})}
						/>
					</div>
				)),
			}}
		/>
	);
}

ValueSelector.propTypes = {
	dim: PropTypes.object.isRequired,
	field: PropTypes.func.isRequired,
	customStats: PropTypes.object.isRequired,
};

ValueSelector.defaultProps = {
};
